import { forwardRef } from "react";
import { RadioButton } from "primereact/radiobutton";
import { Calendar } from "primereact/calendar";
import { Slider } from "primereact/slider";
import { REIDropDown, REIInputNumber, REIInputText, REICheckBox } from "../../../components/ui";
import { ILoan as ILoanInterface } from "../../../interface/tracker";

interface ILoanDetailsTabProps {
  index?: number;
  loan: ILoanInterface;
  errors: { Date: string; InterestRateReviewDate: string; Balance: string; Length: string; InterestRate: string };
  getYearOptions: () => any[];
  getMonthOptions: () => any[];
  displayExtraFields: () => boolean;
  handleInputChange: (field: keyof ILoanInterface, value: any) => void;
  handleLoanStartDateChange: (date: Date | null) => void;
  handleReviewDateChange: (date: Date | null) => void;
  isExistingLoan: boolean;
  onLoanTypeChange: (isExisting: boolean, index: number) => void;
  purchaseType: string;
  isEditMode?: boolean;
}

const LoanDetailsTab = forwardRef<any, ILoanDetailsTabProps>(
  (
    {
      index,
      loan,
      errors,
      getYearOptions,
      getMonthOptions,
      displayExtraFields,
      handleInputChange,
      handleLoanStartDateChange,
      handleReviewDateChange,
      isExistingLoan,
      onLoanTypeChange,
      purchaseType,
    },
    _ref
  ) => {
    return (
      <div>
        {loan.TrackerLoanId == 0 && purchaseType !== "new" && (
          <div className="w-full flex gap-4 my-2">
            <div className="flex items-center">
              <RadioButton
                inputId="existingLoan"
                data-testid={`apld-rdo-existingloan-${loan.uid}`}
                name="loanTypes"
                value={true}
                onChange={() => {
                  onLoanTypeChange(true, index!);
                  handleInputChange("isDisplayExtraFields", false);
                }}
                checked={isExistingLoan}
              />
              <label htmlFor="existingLoan" className="ml-2">
                Existing Loan
              </label>
            </div>
            <div className="flex items-center">
              <RadioButton
                inputId="newLoan"
                data-testid={`apld-rdo-newloan-${loan.uid}`}
                name="loanTypes"
                value={false}
                onChange={() => {
                  onLoanTypeChange(false, index!);
                  handleInputChange("isDisplayExtraFields", true);
                }}
                checked={!isExistingLoan}
              />
              <label htmlFor="newLoan" className="ml-2">
                New Loan
              </label>
            </div>
          </div>
        )}

        <div className="flex flex-row gap-8">
          <div className="w-1/2 flex flex-col gap-4">
            <div className="flex items-center">
              <label htmlFor="lenderName" className="w-1/3">
                Lender/name of loan
              </label>
              <REIInputText
                name="lenderName"
                id={`apld-txt-lendername-${loan.uid}`}
                className="w-2/3"
                type="text"
                value={loan.Name || ""}
                onChange={(e) => handleInputChange("Name", e.target.value)}
              />
            </div>
            <div className="flex items-center">
              <label className="w-1/3">Loan type</label>
              <div className="w-2/3 flex gap-4">
                <div className="flex items-center">
                  <RadioButton
                    inputId="interestOnly"
                    data-testid={`apld-rdo-interestonly-${loan.uid}`}
                    name="loanType"
                    value={0}
                    onChange={(e) => handleInputChange("IsPrincipal", e.value)}
                    checked={loan.IsPrincipal === 0}
                  />
                  <label htmlFor="interestOnly" className="ml-2">
                    Interest only
                  </label>
                </div>
                <div className="flex items-center">
                  <RadioButton
                    inputId="principalAndInterest"
                    data-testid={`apld-rdo-principalinterest-${loan.uid}`}
                    name="loanType"
                    value={1}
                    onChange={(e) => handleInputChange("IsPrincipal", e.value)}
                    checked={loan.IsPrincipal === 1}
                  />
                  <label htmlFor="principalAndInterest" className="ml-2">
                    Principal & Interest
                  </label>
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <label htmlFor="loanLength" className="w-1/3">
                Loan length {purchaseType === "new" ? "" : "remaining (yrs)"}
              </label>
              <div className="flex items-center gap-2">
                <REIDropDown
                  key={`loanYears-${loan.uid}`}
                  id={`apld-drop-loanyears-${loan.uid}`}
                  value={parseInt(loan.Length_Years?.toString() || "30")}
                  data={getYearOptions()}
                  optionValue="value"
                  label="Label"
                  onSelectionChange={(e) => handleInputChange("Length_Years", e.value)}
                  className={`w-1/2 ${errors.Length ? "p-invalid" : ""}`}
                />
                <span className="text-gray-500">yrs</span>
                <REIDropDown
                  key={`loanMonths-${loan.uid}`}
                  id={`apld-drop-loanmonths-${loan.uid}`}
                  value={parseInt(loan.Length_Months?.toString() || "0", 10)}
                  data={getMonthOptions()}
                  optionValue="value"
                  label="Label"
                  onSelectionChange={(e) => handleInputChange("Length_Months", e.value)}
                  className="w-1/2"
                />
                <span className="text-gray-500">months</span>
              </div>
            </div>
            <div className="self-start">{errors.Length && <small className="p-error">{errors.Length}</small>}</div>
            {displayExtraFields() && (
              <div className="flex items-center">
                <label htmlFor="establishmentFees" className="w-1/3">
                  Establishment fees ($)
                </label>
                <div className="w-2/3 flex items-center gap-2">
                  <Slider
                    key={`EstFees-${loan.uid}`}
                    id={`apld-sldr-establishmentfees-${loan.uid}`}
                    data-testid={`apld-sldr-establishmentfees-${loan.uid}`}
                    value={(loan.EstablishmentFees || 0) >= 50000 ? 50000 : loan.EstablishmentFees || 0}
                    onChange={(e: any) => handleInputChange("EstablishmentFees", e.value)}
                    className="w-2/3"
                    max={50000}
                  />
                  <REIInputNumber
                    id={`apld-num-establishmentfees-${loan.uid}`}
                    value={loan.EstablishmentFees || 0}
                    onValueChange={(e) => handleInputChange("EstablishmentFees", e.value || 0)}
                    className="w-1/3"
                  />
                </div>
              </div>
            )}
            {displayExtraFields() && (
              <div className="flex items-center">
                <label htmlFor="otherLoanCosts" className="w-1/3">
                  Other loan costs ($)
                </label>
                <div className="w-2/3 flex items-center gap-2">
                  <Slider
                    key={`OtherLoanCost-${loan.uid}`}
                    id={`apld-sldr-otherloancosts-${loan.uid}`}
                    data-testid={`apld-sldr-otherloancosts-${loan.uid}`}
                    value={(loan.OtherLoanCost || 0) >= 50000 ? 50000 : loan.OtherLoanCost || 0}
                    onChange={(e: any) => handleInputChange("OtherLoanCost", e.value)}
                    className="w-2/3"
                    max={50000}
                  />
                  <REIInputNumber
                    id={`apld-num-otherloancosts-${loan.uid}`}
                    value={loan.OtherLoanCost || 0}
                    onValueChange={(e) => handleInputChange("OtherLoanCost", e.value || 0)}
                    className="w-1/3"
                  />
                </div>
              </div>
            )}
            <div className="flex items-center">
              <label htmlFor="fixedInterestPeriod" className="w-1/3">
                Fixed interest period
              </label>
              <div className="flex items-center gap-2">
                <REIDropDown
                  key={`FixedInterestPeriodYears-${loan.uid}`}
                  id={`apld-drop-fixedintyears-${loan.uid}`}
                  value={parseInt(loan.FixedInterestPeriod_Years?.toString() || "5", 10)}
                  data={getYearOptions()}
                  optionValue="value"
                  label="Label"
                  onSelectionChange={(e) => handleInputChange("FixedInterestPeriod_Years", e.value)}
                  className="w-1/3"
                />
                <span className="text-gray-500">yrs</span>
                <REIDropDown
                  key={`FixedInterestPeriodMonths-${loan.uid}`}
                  id={`apld-drop-fixedintmonths-${loan.uid}`}
                  value={parseInt(loan.FixedInterestPeriod_Months?.toString() || "0", 10)}
                  data={getMonthOptions()}
                  optionValue="value"
                  label="Label"
                  onSelectionChange={(e) => handleInputChange("FixedInterestPeriod_Months", e.value)}
                  className="w-1/3"
                />
                <span className="text-gray-500">months</span>
              </div>
            </div>
          </div>
          <div className="w-1/2 flex flex-col gap-4">
            <div className="flex items-center">
              <label htmlFor="loanRef" className="w-1/3">
                Loan reference #
              </label>
              <REIInputText
                name="loanRef"
                id={`apld-txt-loanref-${loan.uid}`}
                className="w-2/3"
                type="text"
                value={loan.RefCode || ""}
                onChange={(e) => handleInputChange("RefCode", e.target.value)}
              />
            </div>
            <div className="flex flex-col items-center">
              <div className="flex flex-row w-full">
                <label htmlFor="loanStartDate" className="w-1/3">
                  Loan start date
                </label>
                <div className="w-2/3 flex">
                  <Calendar
                    key={`loanStartDate-${loan.uid}`}
                    id="loanStartDate"
                    className={`w-full ${errors.Date ? "p-invalid" : ""}`}
                    value={loan.Date ? new Date(loan.Date) : null}
                    onChange={(e: any) => handleLoanStartDateChange(e.target.value)}
                    mask="99/99/9999"
                    dateFormat="dd/mm/yy"
                    showIcon
                  />
                </div>
              </div>
              <div className="self-end">{errors.Date && <small className="p-error">{errors.Date}</small>}</div>
            </div>
            <div className="flex flex-col items-center w-full">
              <div className="flex flex-row w-full">
                <label htmlFor="loanAmount" className="w-1/3 self-center">
                  {displayExtraFields() ? "Amount of loan ($)" : "Loan balance ($)"}
                </label>
                <div className="flex w-2/3 items-center gap-2">
                  <Slider
                    key={`loanBalance-${loan.uid}`}
                    id={`apld-sldr-loanbalance-${loan.uid}`}
                    data-testid={`apld-sldr-loanbalance-${loan.uid}`}
                    value={(loan.Balance || 0) >= 2000000 ? 2000000 : loan.Balance || 0}
                    onChange={(e: any) => handleInputChange("Balance", e.value)}
                    className="w-2/3"
                    max={2000000}
                  />
                  <REIInputNumber
                    id={`apld-num-loanbalance-${loan.uid}`}
                    value={loan.Balance || 0}
                    onValueChange={(e) => handleInputChange("Balance", e.value || 0)}
                    className="w-1/3"
                  />
                </div>
              </div>
              <div className="self-end">{errors.Balance && <small className="p-error">{errors.Balance}</small>}</div>
            </div>
            {displayExtraFields() && (
              <div className="flex items-center">
                <label htmlFor="valuationFees" className="w-1/3">
                  Valuation fees ($)
                </label>
                <div className="w-2/3 flex items-center gap-2">
                  <Slider
                    key={`ValuationFees-${loan.uid}`}
                    id={`apld-sldr-valuationfees-${loan.uid}`}
                    data-testid={`apld-sldr-valuationfees-${loan.uid}`}
                    value={(loan.ValuationFees || 0) >= 50000 ? 50000 : loan.ValuationFees || 0}
                    onChange={(e: any) => handleInputChange("ValuationFees", e.value)}
                    className="w-2/3"
                    max={50000}
                  />
                  <REIInputNumber
                    id={`apld-num-valuationfees-${loan.uid}`}
                    value={loan.ValuationFees}
                    onValueChange={(e) => handleInputChange("ValuationFees", e.value!)}
                    className="w-1/3"
                  />
                </div>
              </div>
            )}
            <div className="flex flex-row items-center">
              <label htmlFor="interestRate" className="w-1/3">
                Interest rate (%)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  key={`InterestRate-${loan.uid}`}
                  id={`apld-sldr-interestrate-${loan.uid}`}
                  data-testid={`apld-sldr-interestrate-${loan.uid}`}
                  value={loan.InterestRate || 0}
                  onChange={(e: any) => handleInputChange("InterestRate", e.value)}
                  className={`w-2/3 ${errors.InterestRate ? "p-invalid" : ""}`}
                  max={100}
                />
                <REIInputNumber
                  id={`apld-num-interestrate-${loan.uid}`}
                  value={loan.InterestRate || 0}
                  onValueChange={(e) => handleInputChange("InterestRate", e.value || 0)}
                  className="w-1/3"
                  mode="decimal"
                  max={100}
                  maxFractionDigits={2}
                />
              </div>
            </div>
            <div className="self-end">
              {errors.InterestRate && <small className="p-error">{errors.InterestRate}</small>}
            </div>
            <div className="flex flex-col items-center">
              <div className="flex flex-row w-full">
                <label htmlFor="nextRateReviewDate" className="w-1/3">
                  Next rate review date
                </label>
                <div className="w-2/3 flex flex-column">
                  <Calendar
                    key={`nextRateReviewDate-${loan.uid}`}
                    id="nextRateReviewDate"
                    className={`w-full ${errors.InterestRateReviewDate ? "p-invalid" : ""}`}
                    value={loan.InterestRateReviewDate ? new Date(loan.InterestRateReviewDate) : null}
                    onChange={(e: any) => handleReviewDateChange(e.target.value)}
                    mask="99/99/9999"
                    dateFormat="dd/mm/yy"
                    showIcon
                  />
                </div>
              </div>
              <div className="self-end">
                {errors.InterestRateReviewDate && <small className="p-error">{errors.InterestRateReviewDate}</small>}
              </div>
            </div>
            <div className="flex items-center">
              <div className="w-1/3"></div>
              <div className="w-2/3 flex items-center">
                <REICheckBox
                  key={`emailReminder-${loan.uid}`}
                  id={`apld-chk-emailreminder-${loan.uid}`}
                  checked={loan.InterestRateReviewAlert == 1}
                  onChange={(e) => handleInputChange("InterestRateReviewAlert", e.checked ? 1 : 0)}
                />
                <label htmlFor="emailReminder" className="ml-2">
                  Email a reminder before this review date.
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-row items-center">
          {loan.Date && new Date(loan.Date) > new Date() && (
            <div className="w-full text-center  text-red-600 mt-4 bg-gray-100 p-2 rounded-md border border-gray-300">
              Disclaimer : Future dated loans will only appear once the loan start date is reached.
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default LoanDetailsTab;
